// ./src/components/features/PrintableSubmission/PrintableField.tsx
import React from "react";
import { Box, Text, Stack } from "@mantine/core";
import dayjs from "dayjs";

import { FormField } from "@/utils/api";
import { FIELD_TYPE_IDS } from "@/config/fieldTypes";

export interface PrintableFieldProps {
  /** Field definition from form configuration */
  field: FormField;
  /** Submitted value for this field */
  value: any;
  /** Whether to show full credit card number (default: masked) */
  showFullCreditCard?: boolean;
}

/**
 * Masks a credit card number showing only last 4 digits
 * Example: "4111111111111111" -> "••••••••••••1111"
 */
const maskCreditCard = (value: string): string => {
  if (!value || typeof value !== "string") return "";
  const cleaned = value.replace(/\s|-/g, "");
  if (cleaned.length < 4) return "••••";
  const lastFour = cleaned.slice(-4);
  const maskedPortion = "•".repeat(cleaned.length - 4);
  return `${maskedPortion}${lastFour}`;
};

/**
 * Formats a credit card number with spaces for readability
 * Example: "4111111111111111" -> "4111 1111 1111 1111"
 */
const formatCreditCard = (value: string): string => {
  if (!value || typeof value !== "string") return "";
  const cleaned = value.replace(/\s|-/g, "");
  return cleaned.replace(/(.{4})/g, "$1 ").trim();
};

/**
 * Formats a value based on field type (numeric ID)
 */
const formatValue = (
  field: FormField,
  value: any,
  showFullCreditCard: boolean
): string => {
  // Handle null/undefined/empty values
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const fieldType = field.fieldType;

  switch (fieldType) {
    case FIELD_TYPE_IDS.DATE:
      // Date in YYYY-MM-DD format, display in local format
      if (dayjs(value).isValid()) {
        return dayjs(value).format("MMMM D, YYYY");
      }
      return String(value);

    case FIELD_TYPE_IDS.TIME:
      // Time in HH:mm format
      if (value && typeof value === "string") {
        const timeParts = value.split(":");
        if (timeParts.length >= 2) {
          const hours = parseInt(timeParts[0], 10);
          const minutes = timeParts[1];
          const period = hours >= 12 ? "PM" : "AM";
          const displayHours = hours % 12 || 12;
          return `${displayHours}:${minutes} ${period}`;
        }
      }
      return String(value);

    case FIELD_TYPE_IDS.DATETIME:
      // DateTime in ISO format, convert to local time
      if (dayjs(value).isValid()) {
        return dayjs(value).format("MMMM D, YYYY [at] h:mm A");
      }
      return String(value);

    case FIELD_TYPE_IDS.BIRTHDATE:
      // Full birthdate YYYY-MM-DD
      if (dayjs(value).isValid()) {
        return dayjs(value).format("MMMM D, YYYY");
      }
      return String(value);

    case FIELD_TYPE_IDS.BIRTHDAY:
      // Birthday MM-DD format
      if (value && typeof value === "string") {
        const parts = value.split("-");
        if (parts.length === 2) {
          const month = parseInt(parts[0], 10);
          const day = parseInt(parts[1], 10);
          const monthNames = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ];
          if (month >= 1 && month <= 12) {
            return `${monthNames[month - 1]} ${day}`;
          }
        }
      }
      return String(value);

    case FIELD_TYPE_IDS.BOOLEAN:
      // Convert boolean to Yes/No
      if (
        value === true ||
        value === "true" ||
        value === "yes" ||
        value === "1"
      ) {
        return "Yes";
      }
      if (
        value === false ||
        value === "false" ||
        value === "no" ||
        value === "0"
      ) {
        return "No";
      }
      return String(value);

    case FIELD_TYPE_IDS.CREDIT_CARD:
      // Credit card with optional masking
      if (showFullCreditCard) {
        return formatCreditCard(String(value));
      }
      return maskCreditCard(String(value));

    case FIELD_TYPE_IDS.NUMBER:
      // Number formatting
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return num.toLocaleString();
      }
      return String(value);

    case FIELD_TYPE_IDS.MULTIPLE_CHOICE:
      // Multiple choice - array to comma-separated
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      // Handle JSON string
      if (typeof value === "string" && value.startsWith("[")) {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            return parsed.join(", ");
          }
        } catch {
          // Fall through to default
        }
      }
      return String(value);

    case FIELD_TYPE_IDS.COORDINATES:
      // Coordinates - display as "lat, lng"
      if (typeof value === "string") {
        return value;
      }
      if (
        typeof value === "object" &&
        value.lat !== undefined &&
        value.lng !== undefined
      ) {
        return `${value.lat}, ${value.lng}`;
      }
      return String(value);

    case FIELD_TYPE_IDS.SHORT_TEXT:
    case FIELD_TYPE_IDS.LONG_TEXT:
    case FIELD_TYPE_IDS.EMAIL:
    case FIELD_TYPE_IDS.PHONE:
    case FIELD_TYPE_IDS.URL:
    case FIELD_TYPE_IDS.UUID:
    case FIELD_TYPE_IDS.HEX_COLOR:
    case FIELD_TYPE_IDS.PASSWORD:
    case FIELD_TYPE_IDS.USERNAME:
    case FIELD_TYPE_IDS.SLUG:
    case FIELD_TYPE_IDS.REGULAR_EXPRESSION:
    case FIELD_TYPE_IDS.LOOKUP:
      // Plain text display
      return String(value);

    case FIELD_TYPE_IDS.FILE:
      // Files are handled separately by PrintableFileList
      return "";

    case FIELD_TYPE_IDS.ANTISPAMBOT:
    case FIELD_TYPE_IDS.RECAPTCHA2:
    case FIELD_TYPE_IDS.RECAPTCHA3:
    case FIELD_TYPE_IDS.RECAPTCHA_ENTERPRISE:
      // Anti-spam fields - not meaningful to print
      return "";

    default:
      // Default: convert to string
      return String(value);
  }
};

/**
 * Check if field type should be skipped in print output
 */
const shouldSkipField = (fieldType: number | undefined): boolean => {
  if (fieldType === undefined) return false;

  return [
    FIELD_TYPE_IDS.FILE,
    FIELD_TYPE_IDS.ANTISPAMBOT,
    FIELD_TYPE_IDS.RECAPTCHA2,
    FIELD_TYPE_IDS.RECAPTCHA3,
    FIELD_TYPE_IDS.RECAPTCHA_ENTERPRISE,
  ].includes(fieldType);
};

export const PrintableField: React.FC<PrintableFieldProps> = ({
  field,
  value,
  showFullCreditCard = false,
}) => {
  const fieldType = field.fieldType;

  // Skip file and anti-spam fields
  if (shouldSkipField(fieldType)) {
    return null;
  }

  const formattedValue = formatValue(field, value, showFullCreditCard);
  const isEmpty = formattedValue === "";
  const isLongText = fieldType === FIELD_TYPE_IDS.LONG_TEXT;

  // Determine the label to display
  const label = field.label || field.name || "Unnamed Field";

  return (
    <Box
      className="print-field-group"
      py="xs"
      style={{
        borderBottom: "1px solid var(--mantine-color-gray-2)",
      }}
    >
      <Stack gap={4}>
        {/* Field label */}
        <Text
          size="sm"
          fw={600}
          c="dimmed"
          style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
        >
          {label}
        </Text>

        {/* Field value */}
        {isEmpty ? (
          <Text size="sm" c="dimmed" fs="italic">
            No value provided
          </Text>
        ) : isLongText ? (
          <Text
            size="sm"
            style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {formattedValue}
          </Text>
        ) : (
          <Text size="sm" style={{ wordBreak: "break-word" }}>
            {formattedValue}
          </Text>
        )}
      </Stack>
    </Box>
  );
};
